import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Modal } from '../../../app/components/foundation';
import type { Tenant } from '../types/tenant.types';
import { useUpdateTenant } from '../hooks/useUpdateTenant';

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const MOBILE_PATTERN = /^09\d{9}$/;

export interface TenantEditModalProps {
  open: boolean;
  tenant: Tenant;
  onClose: () => void;
  onUpdated: () => void;
}

export function TenantEditModal({
  open,
  tenant,
  onClose,
  onUpdated,
}: TenantEditModalProps) {
  const { submit, submitting, error, resetError } = useUpdateTenant();
  const [name, setName] = useState(tenant.name);
  const [slug, setSlug] = useState(tenant.slug);
  const [mobile, setMobile] = useState(tenant.representative_mobile ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(tenant.name);
    setSlug(tenant.slug);
    setMobile(tenant.representative_mobile ?? '');
    setPassword('');
    setShowPassword(false);
    setValidationError(null);
    resetError();
  }, [open, tenant, resetError]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setValidationError(null);
    const nextName = name.trim();
    const nextSlug = slug.trim().toLowerCase();
    const nextMobile = mobile.trim();

    if (!nextName) return setValidationError('نام تننت الزامی است.');
    if (!SLUG_PATTERN.test(nextSlug)) {
      return setValidationError('دامنه باید فقط شامل حروف انگلیسی، عدد و خط تیره باشد.');
    }
    if (!MOBILE_PATTERN.test(nextMobile)) {
      return setValidationError('شماره موبایل باید با 09 شروع شده و ۱۱ رقم باشد.');
    }
    if (password.length > 0 && password.length < 8) {
      return setValidationError('رمز عبور باید حداقل ۸ کاراکتر باشد.');
    }

    try {
      await submit(
        String(tenant.id),
        { name: nextName, slug: nextSlug },
        { mobile: nextMobile, ...(password ? { password } : {}) },
        {
          name: tenant.name,
          slug: tenant.slug,
          mobile: tenant.representative_mobile ?? '',
        },
      );
      onUpdated();
      onClose();
    } catch {
      // The hook exposes the server error.
    }
  }

  return (
    <Modal open={open} onClose={() => !submitting && onClose()} title="ویرایش تننت">
      <form className="vania-tenant-create-form" onSubmit={handleSubmit}>
        <label className="vania-tenant-create-field">
          <span>نام تننت</span>
          <input value={name} onChange={(event) => setName(event.target.value)} disabled={submitting} />
        </label>
        <label className="vania-tenant-create-field">
          <span>دامنه (slug)</span>
          <input dir="ltr" value={slug} onChange={(event) => setSlug(event.target.value)} disabled={submitting} />
        </label>
        <label className="vania-tenant-create-field">
          <span>موبایل نماینده</span>
          <input dir="ltr" type="tel" value={mobile} onChange={(event) => setMobile(event.target.value)} disabled={submitting} />
        </label>
        <label className="vania-tenant-create-field">
          <span>رمز عبور جدید (اختیاری)</span>
          <div className="vania-tenant-password-field">
            <input
              dir="ltr"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={submitting}
              autoComplete="new-password"
              placeholder="برای حفظ رمز خالی بگذارید"
            />
            <button
              type="button"
              className="vania-tenant-password-toggle"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
              title={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
              disabled={submitting}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        {(validationError || error) && <p className="vania-tenant-create-error">{validationError ?? error}</p>}
        <div className="vania-tenant-create-actions">
          <button type="button" className="vania-tenant-create-cancel" onClick={onClose} disabled={submitting}>انصراف</button>
          <button type="submit" className="vania-tenant-create-submit" disabled={submitting}>
            {submitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
